
import React from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, ExternalLink } from "lucide-react";
import { Header } from "@/components/Header";
import { Card, CardContent } from "@/components/ui/card";
import AdminServiceTabsContent from "@/components/admin/services/AdminServiceTabsContent";
import { useServicePermission } from "@/hooks/useServicePermission";

const ServiceForm = () => {
  const { id } = useParams();
  const isAddMode = !id;
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isAdmin, isOwner, canEdit, loading } = useServicePermission(id);
  const isAuthorized = isAdmin || (isOwner && canEdit) || isAddMode;

  const handleSaveAndReturn = () => {
    toast({
      title: "Success",
      description: isAddMode ? "Service created successfully" : "Service updated successfully",
      variant: "success",
    });
    navigate('/admin/services');
  };

  if (loading && !isAddMode) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading...</p>
      </div>
    );
  }

  if (!isAuthorized && !isAddMode && !loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header showSearchAndLocation={false} />
        <div className="container mx-auto py-6 pt-32">
          <div className="bg-destructive/15 p-4 rounded-md">
            <h2 className="text-lg font-semibold text-destructive">Access Denied</h2>
            <p className="mt-2">
              You don't have permission to edit this service. Only the service owner or an admin can perform these actions.
            </p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={() => navigate('/admin/services')}
            >
              Return to Services
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header showSearchAndLocation={false} />
      <div className="container mx-auto py-6 pt-32">
        <div className="flex flex-col gap-6">
          <div className="flex items-center gap-4">
            <Link to="/admin/services">
              <Button variant="outline" size="sm" className="gap-2">
                <ArrowLeft className="h-4 w-4" />
                Back to Services
              </Button>
            </Link>
            {id && (
              <Link to={`/service/${id}`}>
                <Button variant="ghost" size="sm" className="gap-2">
                  <ExternalLink className="h-4 w-4" />
                  View Service Details
                </Button>
              </Link>
            )}
          </div>

          <h1 className="text-2xl font-bold">
            {isAddMode ? 'Add New Service' : 'Edit Service'}
          </h1>
          
          <Card>
            <CardContent className="pt-6">
              <AdminServiceTabsContent 
                serviceId={id}
                isAuthorized={isAuthorized}
                handleSaveAndReturn={handleSaveAndReturn}
                isAddMode={isAddMode}
              />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ServiceForm;
